import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";
import { Brand, LangSwitcher } from "@/components/kv";

export default function Login() {
  const { t } = useT();
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(username.trim(), password);
      nav(u.role === "admin" ? "/admin" : "/staff", { replace: true });
    } catch (err) {
      setError(errMsg(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative h-56 sm:h-72 lg:h-auto overflow-hidden">
        <img src="/images/hero.jpg" alt="Kon V Palto" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2c1810]/85 via-[#2c1810]/30 to-transparent" />
        <div className="absolute top-5 left-5"><Brand light /></div>
        <p className="absolute bottom-6 left-5 right-5 lg:bottom-12 lg:left-12 font-display italic text-2xl sm:text-3xl lg:text-5xl text-amber-50 max-w-xl leading-tight">
          “{t("login.quote")}”
        </p>
      </div>
      <div className="flex flex-col px-6 sm:px-12 py-8 lg:py-12">
        <div className="flex justify-end"><LangSwitcher /></div>
        <div className="flex-1 flex items-center">
          <form onSubmit={submit} className="w-full max-w-sm mx-auto space-y-6 kv-rise" data-testid="login-form">
            <img src="/images/logo.jpg" alt="Kon V Palto Art Kafe" className="h-28 w-28 rounded-full object-cover border-4 border-white shadow-md" data-testid="login-logo" />
            <div>
              <div className="font-mono-kv text-xs uppercase tracking-[0.2em] text-[#99432b] mb-2">{t("app.tagline")}</div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{t("login.title")}</h1>
              <p className="text-stone-600 mt-2">{t("login.subtitle")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">{t("login.user")}</Label>
              <Input id="username" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)}
                className="h-12 rounded-xl bg-white" data-testid="login-username-input" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("common.password")}</Label>
              <div className="relative">
                <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" autoCapitalize="none" autoCorrect="off"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-white pr-12" data-testid="login-password-input" required />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={t("login.show_pw")} data-testid="login-show-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-full text-stone-500 hover:bg-stone-100">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {error && <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full text-base" data-testid="login-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t("login.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
